// card-panel.ts — the house "card panel" shell primitive.
//
// A card panel is a small, absolutely-positioned floating card: a rounded,
// shadowed box over `var(--bg)` with a 600-weight head line (a counter, when
// present, is baked into the head text), a free-form body, and a wrapping flex
// actions row of buttons. The Labyrinth's suggestion queue (top-right) and
// thread walk card (docked bottom-right or floated next to a node) are both
// card panels.
//
// The primitive owns ONLY the visual shell: the `.card-panel` box, the
// `.card-panel-head` caption, and the `.card-panel-actions` row (styled in
// css/labyrinth.css). It is deliberately positioning-agnostic — `.card-panel`
// sets `position:absolute` and the visuals but no top/right/bottom/left/width —
// so a consumer can dock it in a corner (its own CSS class) or float it at
// computed coordinates (inline `style.left/top`) without the primitive fighting
// either mode. The element is returned directly so the consumer keeps full
// control over positioning, content, show/hide, and event wiring.

/** Options for {@link createCardPanel}. */
export interface CardPanelOptions {
  /**
   * Extra class(es) appended after the base `card-panel` (the consumer's
   * bespoke positioning/width/padding class, e.g. `labyrinth-thread`).
   */
  className?: string;
}

/**
 * Create a card-panel shell element. It starts hidden (`display:none`) and
 * carries `card-panel` plus any `opts.className`. The caller owns appending it,
 * positioning it (corner via CSS or floated via inline `style`), setting its
 * `innerHTML` (use {@link cardHead} / {@link cardActions} for the shared
 * slots), toggling `style.display`, and wiring events.
 */
export function createCardPanel(opts: CardPanelOptions = {}): HTMLDivElement {
  const el = document.createElement('div');
  el.className = opts.className ? `card-panel ${opts.className}` : 'card-panel';
  el.style.display = 'none';
  return el;
}

/**
 * The card head line: a 600-weight, muted caption. Any counter (e.g.
 * `"Merge 2 / 5"`) is baked into `html` by the caller; `html` is inserted
 * verbatim, so the caller is responsible for escaping.
 */
export function cardHead(html: string): string {
  return `<div class="card-panel-head">${html}</div>`;
}

/**
 * The card actions row: a left-aligned, wrapping flex row of buttons.
 * `buttonsHtml` is the consumer's `<button>` markup, inserted verbatim.
 */
export function cardActions(buttonsHtml: string): string {
  return `<div class="card-panel-actions">${buttonsHtml}</div>`;
}
